import React, { useState } from 'react'
import { 
  HStack, 
  Button, 
  Box, 
  Flex, 
  IconButton, 
  Menu, 
  MenuButton, 
  MenuList, 
  MenuItem,
  useBreakpointValue,
  Text
} from '@chakra-ui/react'
import { HamburgerIcon } from '@chakra-ui/icons'
import { useNavigate, useLocation } from 'react-router-dom'

export type AdminNavItem = { label: string; path: string }

type AdminNavProps = {
  items?: AdminNavItem[]
}

const DEFAULT_ITEMS: AdminNavItem[] = [
  { label: '대시보드', path: '/admin/dashboard' },
  { label: '회원 관리', path: '/admin/user' },
  { label: '입금 관리', path: '/admin/deposit' },
  { label: '출금 관리', path: '/admin/withdrawal' },
  { label: '매출 관리', path: '/admin/sales' },
  { label: '산하관리', path: '/admin/subordinate' },
  { label: '봇 관리', path: '/admin/bot' },
  { label: '이력관리', path: '/admin/history' }
]

const AdminNav: React.FC<AdminNavProps> = ({ items = DEFAULT_ITEMS }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  
  // 반응형 설정: md 이상에서는 가로 메뉴, 미만에서는 햄버거 메뉴
  const showHorizontalMenu = useBreakpointValue({ base: false, md: true })

  const handleMenuClick = (path: string) => {
    navigate(path)
    setIsMenuOpen(false)
  }

  const getCurrentMenuLabel = () => {
    const currentItem = items.find(item => item.path === location.pathname)
    return currentItem?.label || '관리자 메뉴'
  }

  return (
    <Box borderBottom="1px solid #e2e8f0">
      {showHorizontalMenu ? (
        // 데스크톱: 가로 메뉴
        <HStack spacing={0}>
          {items.map((menu) => {
            const active = location.pathname === menu.path
            return (
              <Button
                key={menu.path}
                variant="ghost"
                borderRadius={0}
                px={4}
                py={4}
                fontWeight="medium"
                colorScheme="gray"
                fontSize="sm"
                borderBottom={
                  active ? '3px solid #3182ce' : '3px solid transparent'
                }
                onClick={() => navigate(menu.path)}
              >
                {menu.label}
              </Button>
            )
          })}
        </HStack>
      ) : (
        // 모바일: 햄버거 메뉴
        <Flex justify="space-between" align="center" px={4} py={3}>
          <Text fontSize="lg" fontWeight="semibold" color="blue.600">
            {getCurrentMenuLabel()}
          </Text>
          <Menu isOpen={isMenuOpen} onOpen={() => setIsMenuOpen(true)} onClose={() => setIsMenuOpen(false)}>
            <MenuButton
              as={IconButton}
              aria-label="관리자 메뉴"
              icon={<HamburgerIcon />}
              variant="ghost"
              size="sm"
            />
            <MenuList>
              {items.map((menu) => {
                const active = location.pathname === menu.path
                return (
                  <MenuItem
                    key={menu.path}
                    onClick={() => handleMenuClick(menu.path)}
                    bg={active ? 'blue.50' : 'white'}
                    color={active ? 'blue.600' : 'gray.700'}
                    fontWeight={active ? 'semibold' : 'normal'}
                  >
                    {menu.label}
                  </MenuItem>
                )
              })}
            </MenuList>
          </Menu>
        </Flex>
      )}
    </Box>
  )
}

export default AdminNav
